import { type SubmissionResult } from '@conform-to/react'
import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { data, useFetcher } from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { ErrorList, Field, TextareaField } from '#app/components/forms.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { prisma } from '#app/utils/db.server.ts'
import {
	FeedbackCategorySchema,
	FeedbackCategoryValues,
	FeedbackSubmissionSchema,
	FeedbackTypeSchema,
	FeedbackTypeValues,
	getFeedbackCategoryLabel,
	getFeedbackTypeLabel,
	type FeedbackSubmissionInput,
} from '#app/utils/feedback.ts'
import { type Route } from './+types/support.ts'

type FeedbackActionData = {
	status?: 'success' | 'error'
	result?: SubmissionResult<string[]>
	feedbackId?: string
}

export const meta: Route.MetaFunction = () => [
	{ title: 'Support and Feedback | Good to Order' },
	{
		name: 'description',
		content:
			'Report a page issue, suggest a feature, or tell us what would make Good to Order more useful.',
	},
]

export async function loader({ request }: Route.LoaderArgs) {
	const url = new URL(request.url)
	const typeResult = FeedbackTypeSchema.safeParse(url.searchParams.get('type'))
	const categoryResult = FeedbackCategorySchema.safeParse(
		url.searchParams.get('category'),
	)
	const restaurantId = url.searchParams.get('restaurantId') ?? undefined
	const pagePath = url.searchParams.get('pagePath') ?? undefined
	const pageUrl = url.searchParams.get('pageUrl') ?? undefined

	const restaurant = restaurantId
		? await prisma.restaurant.findUnique({
				where: { id: restaurantId },
				select: { id: true, name: true, slug: true },
			})
		: null

	return data({
		prefill: {
			type: typeResult.success ? typeResult.data : 'page_issue',
			category: categoryResult.success ? categoryResult.data : undefined,
			message: '',
			email: '',
			restaurantId,
			pagePath,
			pageUrl,
		} satisfies Omit<FeedbackSubmissionInput, 'email' | 'message'> & {
			email: string
			message: string
		},
		restaurant,
	})
}

export default function SupportRoute({ loaderData }: Route.ComponentProps) {
	const fetcher = useFetcher<FeedbackActionData>()
	const lastResult = fetcher.data?.result as SubmissionResult<string[]> | undefined
	const [form, fields] = useForm({
		id: 'feedback-form',
		constraint: getZodConstraint(FeedbackSubmissionSchema),
		lastResult,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: FeedbackSubmissionSchema })
		},
		shouldRevalidate: 'onBlur',
		defaultValue: loaderData.prefill,
	})

	const currentType = fields.type.value ?? loaderData.prefill.type
	const isPageIssue = currentType === 'page_issue'

	return (
		<div className="min-h-[calc(100vh-8rem)] bg-[#05050a] text-[#f8f8ff]">
			<div className="container mx-auto max-w-3xl px-4 py-14">
				<div className="rounded-3xl border border-[#1a1a2e] bg-[#0d0d15] p-8 shadow-[0_0_30px_rgba(0,224,255,0.08)] sm:p-10">
					<div className="mb-10 space-y-4">
						<p className="text-sm font-semibold uppercase tracking-[0.24em] text-[#7de9ff]">
							Support
						</p>
						<h1 className="text-4xl font-black tracking-tight text-[#f8f8ff]">
							Help improve Good to Order
						</h1>
						<p className="max-w-2xl text-base leading-7 text-[#b0b0e0]">
							Spot something wrong on a page, or want a feature that would
							make this more useful? Send it here.
						</p>
						{loaderData.restaurant ? (
							<div className="rounded-2xl border border-[#1f3255] bg-[#0b1024] px-4 py-3 text-sm text-[#d6dcff]">
								Reporting feedback for{' '}
								<span className="font-semibold">
									{loaderData.restaurant.name}
								</span>
								.
							</div>
						) : null}
						{fetcher.data?.status === 'success' ? (
							<div className="rounded-2xl border border-[#22c55e]/40 bg-[#0b1a12] px-4 py-3 text-sm text-[#bbf7d0]">
								Thanks. Your feedback is in and will be reviewed.
							</div>
						) : null}
					</div>

					<fetcher.Form
						method="post"
						action="/resources/feedback"
						className="space-y-6"
						{...getFormProps(form)}
					>
						<HoneypotInputs />
						<input
							type="hidden"
							name={fields.restaurantId.name}
							value={loaderData.prefill.restaurantId ?? ''}
						/>
						<input
							type="hidden"
							name={fields.pagePath.name}
							value={loaderData.prefill.pagePath ?? ''}
						/>
						<input
							type="hidden"
							name={fields.pageUrl.name}
							value={loaderData.prefill.pageUrl ?? ''}
						/>

						<div className="space-y-2">
							<label
								htmlFor={fields.type.id}
								className="text-sm font-semibold text-[#f8f8ff]"
							>
								What kind of feedback is this?
							</label>
							<select
								id={fields.type.id}
								name={fields.type.name}
								defaultValue={loaderData.prefill.type}
								className="select select-bordered w-full border-[#1a1a2e] bg-[#0b1024] text-[#f8f8ff]"
							>
								{FeedbackTypeValues.map((type) => (
									<option key={type} value={type}>
										{getFeedbackTypeLabel(type)}
									</option>
								))}
							</select>
							<ErrorList errors={fields.type.errors} id={fields.type.errorId} />
						</div>

						{isPageIssue ? (
							<div className="space-y-2">
								<label
									htmlFor={fields.category.id}
									className="text-sm font-semibold text-[#f8f8ff]"
								>
									What is wrong on the page?
								</label>
								<select
									id={fields.category.id}
									name={fields.category.name}
									defaultValue={loaderData.prefill.category ?? ''}
									className="select select-bordered w-full border-[#1a1a2e] bg-[#0b1024] text-[#f8f8ff]"
								>
									<option value="">Choose one</option>
									{FeedbackCategoryValues.map((category) => (
										<option key={category} value={category}>
											{getFeedbackCategoryLabel(category)}
										</option>
									))}
								</select>
								<ErrorList
									errors={fields.category.errors}
									id={fields.category.errorId}
								/>
							</div>
						) : null}

						<TextareaField
							labelProps={{
								htmlFor: fields.message.id,
								children:
									currentType === 'feature_request'
										? 'What would make this more useful?'
										: 'What is wrong, outdated, or missing?',
								className: 'text-sm font-semibold text-[#f8f8ff]',
							}}
							textareaProps={{
								id: fields.message.id,
								name: fields.message.name,
								defaultValue: fields.message.initialValue ?? '',
								rows: 7,
								placeholder:
									currentType === 'feature_request'
										? 'Example: better filters for date spots, cheap eats, or group dinners.'
										: 'Example: the recommendation feels outdated, the map is wrong, or this location has closed.',
								className:
									'min-h-40 border-[#1a1a2e] bg-[#0b1024] text-[#f8f8ff] placeholder:text-[#7070a0]',
							}}
							errors={fields.message.errors}
						/>

						<Field
							labelProps={{
								htmlFor: fields.email.id,
								children: 'Email (optional)',
								className: 'text-sm font-semibold text-[#f8f8ff]',
							}}
							inputProps={{
								...getInputProps(fields.email, { type: 'email' }),
								placeholder:
									'Add an email if you want a follow-up on this feedback.',
								className:
									'border-[#1a1a2e] bg-[#0b1024] text-[#f8f8ff] placeholder:text-[#7070a0]',
							}}
							errors={fields.email.errors}
						/>

						<ErrorList errors={form.errors} id={form.errorId} />

						<div className="flex flex-wrap items-center gap-3">
							<StatusButton
								type="submit"
								status={
									fetcher.state === 'submitting'
										? 'pending'
										: (form.status ?? 'idle')
								}
								disabled={fetcher.state !== 'idle'}
								size="lg"
							>
								Send feedback
							</StatusButton>
							<p className="text-sm text-[#7070a0]">
								Short, specific feedback is the most useful.
							</p>
						</div>
					</fetcher.Form>
				</div>
			</div>
		</div>
	)
}
